import confetti from "assets/animation/confetti-blown-up.json";
import bankConnectionIcon from "assets/images/onboarding/bank-account-connection.svg";
import caledarIcon from "assets/images/onboarding/calendar.svg";
import selfAssessmentEOY from "assets/images/onboarding/eoy-self-assessment.svg";
import expenseTrackingEOY from "assets/images/onboarding/expense-tracking-eoy.svg";
import homeIcon from "assets/images/onboarding/home-icon.svg";
import identityIcon from "assets/images/onboarding/identity.svg";
import mtd from "assets/images/onboarding/mtd.svg";
import phoneIcon from "assets/images/onboarding/phone-icon.svg";
import userIcon from "assets/images/onboarding/user.svg";
import welcomeImage from "assets/images/onboarding/welcome-aboard.svg";
import { FormDataT } from "components/basic/SignUpForm/SignUpForm";
import dayjs from "dayjs";
import { TaxService } from "enums/document";
import { SharingPreferenceIdType } from "types/common";
import { DependT } from "types/questionnaire";
import { isNINValid, isPhoneValid } from "utils/validation";
/* eslint-disable */
export enum OnboardingInputType {
    TEXT = "text",
    EMAIL = "email",
    PHONE = "phone",
    AUTOCOMPLETE_ADDRESS = "autocompleteAddress",
    DATE = "date",
    SELECT_TAX_YEAR = "selectTaxYear",
    SELECT_ONE_TILE = "selectOneTile",
    SELECT_MANY_TILE = "selectManyTile",
}

export enum OnboardingInputIds {
    PHONE = "phone",
    AUTOCOMPLETE_ADDRESS = "autocompleteAddress",
    ADDRESS1 = "address1",
    ADDRESS2 = "address2",
    CITY = "city",
    POSTCODE = "postcode",
    COUNTRY = "country",
    NIN = "nin",
    UTR = "utr",
    EMAIL = "email",
    DOB = "dateOfBirth",
    TAX_YEAR = "taxYear",
    TAX_SERVICE = "taxService",
    SHARING_PREFERENCE = "sharingPreference",
}
/* eslint-enable */

interface IOnboardingInput {
    id: string;
    depend: DependT[];
    label: string;
    type: OnboardingInputType;
    required: boolean;
    placeholder: string;
    suffix?: string;
    helpText?: string;
    options?: { label: string; value: string; showOption: boolean; icon?: string; description?: string }[];
    validate?: (_value: string, _allData?: FormDataT) => string | undefined; // error message or undefined
}

export type OnboardingStep = {
    id: string | number;
    headerTitle?: string;
    title: string;
    description: string;
    image?: string;
    animation?: any;
    inputs?: IOnboardingInput[];
    hasNext: boolean;
    nextAction: "UPDATE_CLIENT" | "DISPATCH_ACTION" | null;
    hasPrev: boolean;
    url?: string;
    depend: DependT[];
};
export const onboardingSteps: OnboardingStep[] = [
    {
        id: "WELCOME",
        headerTitle: "",
        title: "Welcome aboard!",
        description: "You’ve successfully signed up. Let’s get to know you a bit better so we can personalise your experience.",
        image: welcomeImage,
        animation: confetti,
        hasNext: true,
        nextAction: null,
        hasPrev: false,
        depend: [],
    },
    {
        id: "PERSONAL_DETAILS",
        headerTitle: "Account Setup",
        title: "Add Your Details",
        description: "We need these details to set things up.",
        inputs: [
            {
                id: OnboardingInputIds.DOB,
                depend: [],
                type: OnboardingInputType.DATE,
                label: "Date of Birth",
                placeholder: "YYYY/MM/DD",
                required: true,
                suffix: caledarIcon,
                validate: (value: string) => {
                    if (!value) return "Date of Birth is required";
                    const dob = dayjs(value, "YYYY/MM/DD", true);
                    const minDate = dayjs().subtract(18, "years").endOf("year");
                    if (!dob.isValid()) return "Please enter a valid date";
                    if (dob.isAfter(minDate)) return "You must be at least 18 years old";
                },
            },
            {
                id: OnboardingInputIds.NIN,
                depend: [],
                type: OnboardingInputType.TEXT,
                label: "National Insurance Number",
                placeholder: "XK534401A",
                required: true,
                suffix: userIcon,
                validate: (value: string) => {
                    if (!value) return "National Insurance Number is required";
                    if (!isNINValid(value)) return "Enter a valid NIN (e.g. XK534401A)";
                },
            },
            {
                id: OnboardingInputIds.UTR,
                depend: [],
                type: OnboardingInputType.TEXT,
                label: "Unique Taxpayer Reference (UTR)",
                helpText: "You can find your UTR in your HMRC account or on previous tax returns and other HMRC documents.",
                placeholder: "2389212409",
                required: true,
                suffix: identityIcon,
                validate: (value: string) => {
                    if (!value) return "UTR is required";
                    if (!/^\d{10}$/.test(value)) return "Enter a valid 10-digit UTR";
                },
            },
        ],
        hasNext: true,
        nextAction: "UPDATE_CLIENT",
        hasPrev: true,
        depend: [],
    },
    {
        id: "CONTACT_DETAILS",
        headerTitle: "Account Setup",
        title: "Contact Details",
        description: "We only use this for important updates and to verify your tax submission.",
        inputs: [
            {
                id: OnboardingInputIds.PHONE,
                depend: [],
                type: OnboardingInputType.PHONE,
                label: "Contact Number",
                placeholder: "Contact Number",
                required: true,
                suffix: phoneIcon,
                validate: (value: string) => {
                    const trimmed = value?.trim();
                    if (!trimmed || trimmed === "+353" || trimmed === "+44") {
                        return "Please enter a valid contact number";
                    }
                    if (!isPhoneValid(trimmed)) {
                        if (trimmed.startsWith("+353")) {
                            return "Please enter a valid mobile number. Ex: +353 822 334455";
                        }
                        if (trimmed.startsWith("+44")) {
                            return "Please enter a valid mobile number. Ex: +44 2071 234567";
                        }
                        return "Invalid phone number";
                    }
                },
            },
            {
                id: OnboardingInputIds.AUTOCOMPLETE_ADDRESS,
                type: OnboardingInputType.AUTOCOMPLETE_ADDRESS,
                label: "Home Address",
                placeholder: "Type your home address",
                required: false,
                suffix: homeIcon,
                depend: [],
            },
            {
                id: OnboardingInputIds.ADDRESS1,
                type: OnboardingInputType.TEXT,
                label: "Address Line 1",
                placeholder: "Address Line 1",
                required: true,
                validate: (value: string) => {
                    if (!value.trim()) return "Address line 1 is required";
                },
                depend: [
                    { id: "address1", opposite: true, value: undefined },
                    {
                        id: "address1",
                        value: "",
                        orDepend: true,
                    },
                ],
                suffix: homeIcon,
            },
            {
                id: OnboardingInputIds.ADDRESS2,
                type: OnboardingInputType.TEXT,
                label: "Address Line 2",
                placeholder: "Address Line 2",
                required: false,
                depend: [
                    { id: OnboardingInputIds.ADDRESS2, opposite: true, value: undefined },
                    {
                        id: OnboardingInputIds.ADDRESS2,
                        value: "",
                        orDepend: true,
                    },
                ],
                suffix: homeIcon,
            },
            {
                id: OnboardingInputIds.CITY,
                type: OnboardingInputType.TEXT,
                label: "City",
                placeholder: "City",
                required: true,
                validate: (value: string) => {
                    if (!value.trim()) return "City is required";
                },
                depend: [
                    { id: OnboardingInputIds.CITY, opposite: true, value: undefined },
                    {
                        id: OnboardingInputIds.CITY,
                        value: "",
                        orDepend: true,
                    }],
                suffix: homeIcon,
            },
            {
                id: OnboardingInputIds.POSTCODE,
                type: OnboardingInputType.TEXT,
                label: "Postcode",
                placeholder: "Postcode",
                required: true,
                validate: (value: string) => {
                    if (!value) return "Postcode is required";
                },
                depend: [
                    { id: OnboardingInputIds.POSTCODE, opposite: true, value: undefined },
                    {
                        id: OnboardingInputIds.POSTCODE,
                        value: "",
                        orDepend: true,
                    },
                ],
                suffix: homeIcon,
            },

        ],
        hasNext: true,
        nextAction: "UPDATE_CLIENT",
        hasPrev: true,
        depend: [],
    },
    {
        id: "TAX_YEAR_AND_SERVICE",
        headerTitle: "Account Setup",
        title: "Select Tax Year & Service",
        description: "Choose the tax year and service you need help with, so we can tailor everything to you.",
        inputs: [
            {
                id: OnboardingInputIds.TAX_YEAR,
                depend: [],
                type: OnboardingInputType.SELECT_TAX_YEAR,
                label: "Tax Year",
                placeholder: "Select Tax Year",
                required: true,
            },
            {
                id: OnboardingInputIds.TAX_SERVICE,
                depend: [],
                type: OnboardingInputType.SELECT_ONE_TILE,
                label: "Select the service you are looking for?",
                placeholder: "Select Service",
                required: true,
                options: [
                    { label: "Making Tax Digital (MTD) for Income Tax", value: TaxService.MTD, showOption: false, icon: mtd, description: "Get help with your MTD for Income Tax obligations" },
                    { label: "End of Year Self Assessment", value: TaxService.EOY_SELF_ASSESSMENT, showOption: true, icon: selfAssessmentEOY, description: "Get help with your end of year self assessment" },
                    { label: "Expense Tracking & End of Year Self Assessment", value: TaxService.EOY_EXPENSE_TRACKING, showOption: false, icon: expenseTrackingEOY, description: "Track your expenses and get help with your end of year self assessment" },
                ],
            },
        ],
        hasNext: true,
        nextAction: "DISPATCH_ACTION",
        url: "/profile",
        hasPrev: true,
        depend: [],
    },
    {
        id: "BANK_ACCOUNT_CONNECTION",
        headerTitle: "Bank Account Connection",
        title: "How would you prefer to share your income and tax details?",
        description: "Connect your bank for automatic tracking and smart expense claims or enter details manually if you don’t use a business account.",
        inputs: [
            {
                id: OnboardingInputIds.SHARING_PREFERENCE,
                depend: [
                    { id: OnboardingInputIds.TAX_SERVICE, value: TaxService.MTD, opposite: true },
                    { id: OnboardingInputIds.TAX_SERVICE, value: TaxService.EOY_EXPENSE_TRACKING, opposite: true },
                ],
                type: OnboardingInputType.SELECT_ONE_TILE,
                label: "How do you want to manage your expenses?",
                placeholder: "Select Service",
                required: true,
                options: [
                    { label: "Connect Bank Account", value: SharingPreferenceIdType.connect, showOption: true, icon: bankConnectionIcon, description: "Securely link your business bank account. We’ll automatically import your transactions, help you categorise them, and make sure you don’t miss any claimable expenses." },
                    { label: "Continue Manually", value: SharingPreferenceIdType.manual, showOption: true, icon: expenseTrackingEOY, description: "Add each business transaction yourself. useful if you track your income and expenses outside of a bank account." },
                ],
            },
        ],
        hasNext: true,
        nextAction: "DISPATCH_ACTION",
        url: "/profile",
        hasPrev: true,
        depend: [
            { id: OnboardingInputIds.TAX_SERVICE, value: TaxService.MTD, opposite: true },
            { id: OnboardingInputIds.TAX_SERVICE, value: TaxService.EOY_EXPENSE_TRACKING, opposite: true },
        ],
    },
];
